import {getSelectableDivisionIds} from 'src/modules/auth/currentDivisionScope';
import getStore from 'src/modules/store';

/** Rol fijo de super admin del tenant (`Roles.values.admin`). */
export function isTenantFixedAdminRole(roles: string[] | null | undefined): boolean {
  return Array.isArray(roles) && roles.includes('admin');
}

/**
 * Muestra el selector manual de división en formularios.
 * Solo usuarios con rol fijo `admin` del tenant; el resto sigue usando solo la política al guardar.
 */
export function canShowDivisionIdFieldInForm(
  _tenantAccessContext: {isDivisionTransversalGlobal?: boolean} | null | undefined,
  isFixedRoleAdmin: boolean,
): boolean {
  return isFixedRoleAdmin === true;
}

function divisionOptionFromRaw(raw: unknown): {id: string; label: string} | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string' && raw) {
    return {id: raw, label: raw};
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const d = raw as Record<string, unknown>;
    const id = d.id ?? d._id;
    if (id == null) {
      return null;
    }
    const label = d.nombre ?? d.name ?? d.label ?? d.code ?? String(id);
    return {id: String(id), label: String(label)};
  }
  return null;
}

/**
 * Valor inicial para `divisionId` en react-hook-form (objeto { id, label } o null).
 */
export function mapRecordToFormDivisionId(record: {divisionId?: unknown} | null | undefined): {id: string; label: string} | null {
  return divisionOptionFromRaw(record?.divisionId);
}

/**
 * Valor inicial para el selector (admin): `divisionId` del registro, o si falta la división actual del tenant.
 * Si no se pasa `currentDivisionIdHint`, se lee `currentDivisionId` del store (útil dentro de `useState` inicial).
 */
export function mapRecordOrCurrentDivisionToFormDivisionId(
  record: {divisionId?: unknown} | null | undefined,
  currentDivisionIdHint?: string | null,
): {id: string; label: string} | null {
  const fromRecord = mapRecordToFormDivisionId(record);
  if (fromRecord) {
    return fromRecord;
  }
  let id =
    currentDivisionIdHint != null && currentDivisionIdHint !== '' ? String(currentDivisionIdHint) : null;
  if (!id) {
    try {
      const sid = getStore()?.getState?.()?.auth?.currentDivisionId;
      id = sid != null && sid !== '' ? String(sid) : null;
    } catch {
      id = null;
    }
  }
  if (id) {
    return {id, label: id};
  }
  return null;
}

function parseDivisionIdFromPayload(raw: unknown): string | undefined {
  if (raw == null) {
    return undefined;
  }
  if (typeof raw === 'string' && raw) {
    return raw;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const id = (raw as any).id ?? (raw as any)._id;
    return id != null ? String(id) : undefined;
  }
  return undefined;
}

function stripLegacyDivisionIdsKey(payload: Record<string, any>) {
  if (Object.prototype.hasOwnProperty.call(payload, 'divisionIds')) {
    delete payload.divisionIds;
  }
}

/**
 * Alinea `divisionId` con el contexto de acceso (equivalente a `applyDivisionIdForEntityWrite` en backend).
 */
export function applyDivisionIdPolicyToPayload(
  payload: Record<string, any>,
  tenantAccessContext:
    | {
        isDivisionTransversalGlobal?: boolean;
        directDivisionIds?: string[];
        effectiveDivisionIds?: string[];
      }
    | null
    | undefined,
  opts?: {treatAsTransversalSubmit?: boolean},
): void {
  const hasDivisionIdKey = Object.prototype.hasOwnProperty.call(payload, 'divisionId');

  const useTransversalPath =
    !tenantAccessContext || tenantAccessContext.isDivisionTransversalGlobal === true || opts?.treatAsTransversalSubmit === true;

  if (useTransversalPath) {
    if (!hasDivisionIdKey) {
      stripLegacyDivisionIdsKey(payload);
      return;
    }
    const parsed = parseDivisionIdFromPayload(payload.divisionId);
    if (parsed) {
      payload.divisionId = parsed;
    } else {
      payload.divisionId = undefined;
    }
    stripLegacyDivisionIdsKey(payload);
    return;
  }

  const allowed = getSelectableDivisionIds(tenantAccessContext);
  if (!allowed.length) {
    stripLegacyDivisionIdsKey(payload);
    return;
  }

  const explicit = hasDivisionIdKey ? parseDivisionIdFromPayload(payload.divisionId) : undefined;

  let storeId: string | null = null;
  try {
    const id = getStore()?.getState?.()?.auth?.currentDivisionId;
    storeId = id != null && id !== '' ? String(id) : null;
  } catch {
    storeId = null;
  }
  const preferred = storeId && allowed.includes(storeId) ? storeId : null;

  if (explicit && allowed.includes(explicit)) {
    payload.divisionId = explicit;
  } else {
    payload.divisionId = preferred ?? allowed[0];
  }
  stripLegacyDivisionIdsKey(payload);
}
